"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import styles from "./ArchitectureSection.module.scss";
import Link from "next/link";
import Button from "../../Common/Button/Button";
import {
  JourneyVisual,
  WorkflowVisual,
  ActivitiesVisual,
  FoundationVisual,
  AutonomyVisual,
  SynergyVisual,
  ScaleVisual,
} from "./ArchitectureVisuals";

type LayerVisual = "journey" | "workflow" | "activities" | "foundation";

const layerVisuals: Record<LayerVisual, React.ReactNode> = {
  journey: <JourneyVisual />,
  workflow: <WorkflowVisual />,
  activities: <ActivitiesVisual />,
  foundation: <FoundationVisual />,
};

type FeatureKey = "autonomous" | "humanAi" | "scalable";

const featureIcons: Record<FeatureKey, React.ReactNode> = {
  autonomous: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
    </>
  ),
  humanAi: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 6.5a3 3 0 0 1 0 5.6" />
      <path d="M20.5 20a5.6 5.6 0 0 0-4-5.3" />
    </>
  ),
  scalable: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
    </>
  ),
};

const featureVisuals: Record<FeatureKey, React.ReactNode> = {
  autonomous: <AutonomyVisual />,
  humanAi: <SynergyVisual />,
  scalable: <ScaleVisual />,
};

const featureData: { key: FeatureKey; title: string; desc: string }[] = [
  {
    key: "autonomous",
    title: "Autonomous Intelligence",
    desc: "AI agents that think, adapt and make intelligent decisions to optimize customer experiences.",
  },
  {
    key: "humanAi",
    title: "Human-AI Synergy",
    desc: "Seamless collaboration between human expertise and AI capabilities for superior outcomes.",
  },
  {
    key: "scalable",
    title: "Scalable Automation",
    desc: "Enterprise-grade automation that scales with your business and adapts to market changes.",
  },
];

const topActors: {
  icon: string;
  label: string;
  color: string;
  live?: boolean;
}[] = [
  { icon: "brain.svg", label: "AI Agents", color: "#3b82f6" },
  { icon: "users.svg", label: "People", color: "#10b981" },
  { icon: "setting.svg", label: "Automation", color: "#8b5cf6", live: true },
];

const integrations = [
  { icon: "globe.svg", label: "Web", color: "#3b82f6" },
  { icon: "cart.svg", label: "Commerce", color: "#10b981" },
  { icon: "bar.svg", label: "Analytics", color: "#f59e0b" },
  { icon: "message.svg", label: "Chat", color: "#ef4444" },
  { icon: "setting.svg", label: "ERP", color: "#8b5cf6" },
  { icon: "users.svg", label: "CRM", color: "#06b6d4" },
  { icon: "heart.svg", label: "Loyalty", color: "#ec4899" },
  { icon: "star.svg", label: "Reviews", color: "#84cc16" },
  { icon: "current.svg", label: "Payments", color: "#f97316" },
  { icon: "target.svg", label: "Ads", color: "#14b8a6" },
];

const architectureData: {
  title: string;
  items: string[];
  style: string;
  visual: LayerVisual;
}[] = [
  {
    title: "COMMERCE PROCESSES",
    items: [
      "Customer Journey Orchestration",
      "Personalization Engine",
      "Commerce Intelligence",
    ],
    style: styles.commerceProcesses,
    visual: "journey",
  },
  {
    title: "INTELLIGENT WORKFLOWS",
    items: [
      "Agentic Pricing Plans",
      "Human-AI Collaboration",
      "Decision Automation",
    ],
    style: styles.intelligentWorkflows,
    visual: "workflow",
  },
  {
    title: "COMMERCE ACTIVITIES",
    items: [
      "Product Discovery",
      "Cart & Checkout",
      "Customer Support",
      "Order Fulfillment",
    ],
    style: styles.commerceActivities,
    visual: "activities",
  },
  {
    title: "FOUNDATION",
    items: [
      "Security & Compliance",
      "Data & Analytics",
      "AI Models & Control",
      "Platform Integration",
    ],
    style: styles.foundation,
    visual: "foundation",
  },
];

function ArchitectureSection() {
  const diagramRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  // Reveal + animate the diagram once it scrolls into view.
  useEffect(() => {
    const node = diagramRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Cursor-following spotlight for interactive cards/layers.
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${event.clientX - rect.left}px`);
    el.style.setProperty("--my", `${event.clientY - rect.top}px`);
  };

  return (
    <section className={styles.architecture}>
      <div className="container">
        <div className={styles.architectureContent}>
          <h2 className={styles.iconContainer}>
            Socommerz Agentic{" "}
            <span className={styles.relative}>
              Commerce
              <span className={styles.iconWrapperBlack}>
                <Image
                  src="/agent-icons/sparkle.svg"
                  alt="Sparkle Icon"
                  width={12}
                  height={12}
                />
              </span>{" "}
            </span>{" "}
            Architecture
          </h2>

          <p className={styles.subtitle}>
            A comprehensive platform that orchestrates AI agents, human
            expertise, and automated systems for intelligent commerce
            experiences.
          </p>

          <div
            ref={diagramRef}
            className={`${styles.architectureDiagram} ${
              revealed ? styles.revealed : ""
            }`}
          >
            {/* Top Icons */}
            <div className={styles.topIcons}>
              {topActors.map((actor, i) => (
                <div
                  key={actor.label}
                  className={styles.archIcon}
                  style={{ "--c": actor.color, "--i": i } as React.CSSProperties}
                >
                  <span
                    className={styles.archGlyph}
                    style={
                      {
                        WebkitMaskImage: `url(/agent-icons/${actor.icon})`,
                        maskImage: `url(/agent-icons/${actor.icon})`,
                      } as React.CSSProperties
                    }
                  />
                  <div className={styles.iconLabel}>{actor.label}</div>
                  {actor.live && (
                    <div className={styles.liveBadge}>
                      <span className={styles.liveDot} />
                      Live
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Layered Architecture */}
            <div className={styles.architectureLayers}>
              <span className={styles.flowLine} aria-hidden="true">
                <span className={styles.flowPulse} />
              </span>
              {architectureData.map(({ title, items, style, visual }, index) => (
                <div
                  key={index}
                  className={`${styles.layer} ${style}`}
                  onMouseMove={handleMouseMove}
                  style={{ "--i": index } as React.CSSProperties}
                >
                  <div className={styles.layerGlow} aria-hidden="true" />
                  <div className={styles.layerMain}>
                    <div className={styles.layerTitle}>{title}</div>
                    <div className={styles.layerItems}>
                      {items.map((item, i) => (
                        <div key={i} className={styles.layerItem}>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles.layerVisual} aria-hidden="true">
                    {layerVisuals[visual]}
                  </div>
                </div>
              ))}
            </div>

            {/* Integration layer */}
            <div className={styles.integrationBar}>
              <div className={styles.integrationTitle}>
                Systems, Applications, &amp; Data Integration
              </div>
              <div className={styles.integrationTrack}>
                <span className={styles.integrationLine} aria-hidden="true">
                  <span className={styles.integrationPulse} />
                </span>
                <div className={styles.integrationIcons}>
                  {integrations.map(({ icon, label, color }, i) => (
                    <div className={styles.intItem} key={i}>
                      <div
                        className={styles.integrationIcon}
                        style={{ "--c": color } as React.CSSProperties}
                      >
                        <span
                          className={styles.intGlyph}
                          style={
                            {
                              WebkitMaskImage: `url(/agent-icons/${icon})`,
                              maskImage: `url(/agent-icons/${icon})`,
                            } as React.CSSProperties
                          }
                        />
                      </div>
                      <span className={styles.intLabel}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Feature Section */}
            <div className={styles.bottomFeatures}>
              {featureData.map((feat, i) => (
                <div
                  key={i}
                  className={`${styles.featureBox} ${styles[feat.key]}`}
                  onMouseMove={handleMouseMove}
                  style={{ "--i": i } as React.CSSProperties}
                >
                  <div className={styles.featureGlow} aria-hidden="true" />
                  <div className={styles.featureIcon}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {featureIcons[feat.key]}
                    </svg>
                  </div>
                  <h3>{feat.title}</h3>
                  <p>{feat.desc}</p>
                  <div className={styles.featureVisual} aria-hidden="true">
                    {featureVisuals[feat.key]}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Link href="/agentic-commerce" className={styles.btnClass}>
            <Button text="Know More" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default ArchitectureSection;
